import { useCallback } from "react";

import { faTrashAlt } from "@fortawesome/pro-regular-svg-icons";
import { useFormContext } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { StringEditor } from "src/base-components/EditorTable/StringEditor";
import { EditorCellProps } from "src/base-components/EditorTable/types";
import { IconButton } from "src/design-system/Icon";
import { useIsFieldHighlighted } from "src/flowGraph/canvasSearch/CanvasSearchHighlightContext";
import { useFactorContext } from "src/scorecardNode/context";
import { FactorCodeInput } from "src/scorecardNode/FactorCodeInput";
import { FormShape } from "src/scorecardNode/types";

type Props<T> = EditorCellProps<T, { id: string; value: string }> & {
  alignCenter?: boolean;
  removable?: boolean;
  prefix: "input_field" | "effect";
};

export const CellStringEditor = <T,>({
  prefix,
  context,
  state,
  alignCenter = true,
  removable = true,
  ...props
}: Props<T>) => {
  const { watch, setValue, getValues } = useFormContext<FormShape>();
  const {
    canRemoveCase,
    fallbackId,
    onRemoveCase,
    index: factorIndex,
  } = useFactorContext();
  const caseIndex = (() => {
    if (!("row" in context)) {
      return 0;
    }
    const index = context.row.index;
    return context.row.parentId ? index + 1 : index;
  })();

  const field = (() => {
    if ("row" in context && context.row.id === fallbackId) {
      return `factors.${factorIndex}.fallback` as const;
    }

    return prefix === "input_field"
      ? (`factors.${factorIndex}.input_field` as const)
      : (`factors.${factorIndex}.cases.${caseIndex}.effect` as const);
  })();

  const fieldName = `${field}.value` as const;

  const value = watch(fieldName);
  const fieldId = getValues(`${field}.id`);
  const isSearchHighlighted = useIsFieldHighlighted(fieldId);

  const handleStringChange = useCallback(
    (updatedStringValue: string) => {
      setValue(fieldName, updatedStringValue);
    },
    [setValue, fieldName],
  );

  return (
    <div className="flex w-full items-center">
      {prefix === "input_field" ? (
        <FactorCodeInput
          isSearchHighlighted={isSearchHighlighted}
          placeholder={props.columnDef.cell.placeholder}
          state={state}
          value={value}
          onChange={handleStringChange}
        />
      ) : (
        <StringEditor
          {...props}
          alignCenter={alignCenter}
          isSearchHighlighted={isSearchHighlighted}
          placeholder={props.columnDef.cell.placeholder}
          state={state}
          value={value}
          onChange={handleStringChange}
        />
      )}

      {removable &&
        canRemoveCase &&
        "row" in context &&
        context.row.id !== fallbackId && (
          <span
            className={twMerge(
              "absolute right-[-15px] z-50 cursor-pointer",
              "rounded border border-gray-300 bg-white shadow-sm",
              "opacity-0 transition-opacity duration-300 hover:opacity-100",
              state.isRowHovered && "opacity-100",
            )}
          >
            <IconButton
              icon={faTrashAlt}
              size="xs"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                onRemoveCase(context.row.id);
              }}
            />
          </span>
        )}
    </div>
  );
};
